if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        this.thInit();
        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if ((this.dataset.desktopLayout.includes('thumbnail') || this.dataset.desktopLayout === 'carousel') && this.mql.matches)
          this.removeListSemantic();
      }

      // THEMERIE: the carousel's arrows sit on the photo and move the photo itself (Dawn's strip arrows only
      // scrolled the thumbnails, so with a row that fits they did nothing). Arrow keys do the same. The counter,
      // and the progress line or dots on phones, follow the shown photo.
      thInit() {
        this.thArrows = Array.from(this.querySelectorAll('[data-gallery-step]'));
        this.thArrows.forEach((button) =>
          button.addEventListener('click', () => this.thStep(Number(button.dataset.galleryStep)))
        );
        if (this.thArrows.length) {
          this.elements.viewer.addEventListener('keydown', (event) => {
            if (!this.mql.matches || event.target.closest('input, select, textarea')) return;
            if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
            event.preventDefault();
            this.thStep(event.key === 'ArrowRight' ? 1 : -1);
          });
        }
        this.elements.viewer.addEventListener('slideChanged', (event) => {
          const mediaId = event.detail.currentElement?.dataset.mediaId;
          // a thumbnail click scrolls the photos past the ones in between: follow only the photo it heads for
          if (this.thHeading) {
            if (mediaId !== this.thHeading) return;
            this.thHeading = null;
          }
          this.thPaint(event.detail.currentPage - 1);
          // follow a swipe at once (Dawn waits 500ms before moving the thumbnail highlight). By media id, not by
          // position: the thumbnails of the other variants stay in the strip (hidden) but are no slides.
          const thumbnail = this.elements.thumbnails?.querySelector(`[data-target="${mediaId}"]`);
          if (thumbnail) this.setActiveThumbnail(thumbnail);
        });
        // a swipe of the shopper's own ends the wait for a clicked photo
        this.elements.viewer.addEventListener('touchstart', () => (this.thHeading = null), { passive: true });
        this.thPaint(this.thIndex());
        this.thInitIndicator();
      }

      // one frame that glides between thumbnails instead of the border jumping (border and underline styles)
      thInitIndicator() {
        const strip = this.elements.thumbnails;
        const style = this.dataset.thumbStyle;
        if (!strip || (style !== 'border' && style !== 'underline')) return;
        this.thList = strip.querySelector('.thumbnail-list');
        if (!this.thList) return;
        this.thIndicator = document.createElement('span');
        this.thIndicator.className = 'th-thumb-indicator is-instant';
        this.thIndicator.setAttribute('aria-hidden', 'true');
        this.thList.appendChild(this.thIndicator);
        strip.classList.add('has-indicator');
        this.thMoveIndicator();
        requestAnimationFrame(() => requestAnimationFrame(() => this.thIndicator.classList.remove('is-instant')));
        new ResizeObserver(() => this.thMoveIndicator(true)).observe(this.thList);
      }

      thMoveIndicator(instant = false) {
        if (!this.thIndicator) return;
        const button = this.thList.querySelector('.thumbnail[aria-current="true"]');
        if (!button || !button.offsetParent) return (this.thIndicator.style.opacity = '0');
        const list = this.thList.getBoundingClientRect();
        const box = button.getBoundingClientRect();
        const x = box.left - list.left + this.thList.scrollLeft;
        const y = box.top - list.top + this.thList.scrollTop;
        const underline = this.dataset.thumbStyle === 'underline';
        this.thIndicator.classList.toggle('is-instant', instant);
        this.thIndicator.style.opacity = '1';
        this.thIndicator.style.width = `${box.width}px`;
        this.thIndicator.style.height = underline ? '2px' : `${box.height}px`;
        this.thIndicator.style.transform = `translate(${x}px, ${underline ? y + box.height + 4 : y}px)`;
      }

      thItems() {
        return Array.from(this.elements.viewer.querySelectorAll('.product__media-list > [data-media-id]'));
      }

      thIndex() {
        return Math.max(0, this.thItems().findIndex((item) => item.classList.contains('is-active')));
      }

      thStep(direction) {
        const items = this.thItems();
        const next = items[Math.min(items.length - 1, Math.max(0, this.thIndex() + direction))];
        if (next) this.setActiveMedia(next.dataset.mediaId, false);
      }

      thPaint(index) {
        const total = this.thItems().length;
        this.querySelectorAll('[data-gallery-current]').forEach((el) => (el.textContent = index + 1));
        if (this.thArrows.length) {
          this.thArrows.forEach((button) => {
            const step = Number(button.dataset.galleryStep);
            button.disabled = step < 0 ? index <= 0 : index >= total - 1;
          });
        }
        const marks = this.querySelectorAll('.th-gallery-pager__marks i');
        const progress = this.dataset.mobileNav === 'progress';
        marks.forEach((mark, k) => mark.classList.toggle('is-on', progress ? k <= index : k === index));
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            const list = activeMedia.parentElement;
            if (list.scrollWidth > list.clientWidth && Math.abs(list.scrollLeft - activeMedia.offsetLeft) > 1)
              this.thHeading = activeMedia.dataset.mediaId;
            list.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);
        this.thPaint(this.thItems().indexOf(activeMedia));

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        this.thMoveIndicator();
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
